import _ from 'underscore';

import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  SimpleTableSortDirection,
  SimpleTableSortState,
} from 'ui-library/simple-table/useSimpleTableSortState';
import count from 'util/count';
import sortedBy from 'util/sortedBy';

import cellValueToUserFacingString from './cellValueToUserFacingString';
import {createUnvalidatedAccessor, DataFrameValueAccessor} from './createAccessors';
import findColumn from './findColumn';
import isNumericCellValue from './isNumericCellValue';

const REVERSED_ORDER_LEAF_TYPES: Array<LeafType> = [LeafType.DATE, LeafType.DATETIME, LeafType.BOOLEAN];
const REVERSED_ORDER_SYSTEM_COLUMN_NAMES: Array<string> = [SystemColumnName.IS_EXCLUDED];

/**
 * Return a copy of the input data frame sorted based on the given accessors.
 *
 * Note: This is an expensive operation on larger data frames, so be sure to reduce how frequently it's called
 * by memoizing. If this isn't possible or sufficient, consider implementing `lruMemoize` here.
 */
export function sortDataFrameByAccessors(
  dataFrame: DataFrame,
  accessors: Array<DataFrameValueAccessor<string | number>>,
  ascending: boolean = true
): DataFrame {
  return {
    ...dataFrame,
    rows: sortedBy(
      dataFrame.rows,
      accessors.map((accessor) => ({accessor, reversed: !ascending}))
    ),
  };
}

function sortIsReversed(column: DataFrameColumnDef, sortDirection: SimpleTableSortDirection): boolean {
  const numReversals = count(
    [
      sortDirection === 'arrow-down',
      REVERSED_ORDER_LEAF_TYPES.includes(column.type.leafType),
      REVERSED_ORDER_SYSTEM_COLUMN_NAMES.includes(column.systemName),
    ],
    true
  );
  return numReversals % 2 === 1;
}

/**
 * Return a copy of the input data frame sorted based on a `SimpleTableSortState`. This function is mostly aimed
 * for sorting data frames in the context of a table that supports column sorting.
 *
 * Columns of API IDs sort by their resolved names if `tableMode === DataFrameTableMode.RICH`.
 *
 * Lists sort by the string representation of the whole list. The order of elements in lists is relevant,
 * as lists are not first internally sorted.
 *
 * Note: This is an expensive operation on larger data frames, so be sure to reduce how frequently it's called
 * by memoizing. If this isn't possible or sufficient, consider implementing `lruMemoize` here.
 */
export default function sortDataFrame(
  dataFrame: DataFrame,
  sortState: SimpleTableSortState,
  tableMode: DataFrameTableMode = DataFrameTableMode.TEXT
): DataFrame {
  if (sortState == null) {
    return dataFrame;
  }

  const column = findColumn(dataFrame, sortState.columnKey as string);
  if (column == null) {
    return dataFrame;
  }
  const accessor = createUnvalidatedAccessor(column);

  const {rows} = dataFrame;
  const {sortDirection} = sortState;

  const reversed = sortIsReversed(column, sortDirection);

  const [nullRows, nonNullRows] = _.partition(rows, (row) => accessor(row) == null);

  // Separate out values that are either JS Numbers or convertible to JS Numbers.
  const [numericRows, nonNumericRows] = _.partition(nonNullRows, (row) => {
    const cellValue = accessor(row);
    return isNumericCellValue(cellValue, column);
  });

  // We use this as the last keyAccessor in all `sortedBy` calls below so that ties are broken by row ID.
  const idSortAccessor = {
    accessor: SystemColumnName.ROW_ID,
    naturalSort: true,
    reversed,
  };

  const sortedNumericRows = sortedBy(numericRows, [
    // We can use Number here because parseNumber has already succeeded on numericRows above.
    {accessor: (row) => Number(accessor(row)), reversed},
    idSortAccessor,
  ]);

  const sortedNonNumericRows = sortedBy(nonNumericRows, [
    {
      accessor: (row) =>
        cellValueToUserFacingString(accessor(row), column, dataFrame.hydratedProperties, tableMode),
      naturalSort: true,
      reversed,
    },
    idSortAccessor,
  ]);

  const sortedNullRows = sortedBy(nullRows, [idSortAccessor]);

  const sortedRows = reversed
    ? [...sortedNonNumericRows, ...sortedNumericRows, ...sortedNullRows]
    : [...sortedNullRows, ...sortedNumericRows, ...sortedNonNumericRows];

  return {...dataFrame, rows: sortedRows};
}
